import React from "react";
import styled, { keyframes } from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import MainContainer from "../components/MainContainer";

const TAPE_ITEMS = "404 ◆ Stranica ne postoji ◆ 迷 ◆ Vrati se na put ◆ ";

const NotFoundPage = () => {
  const navigate = useNavigate();

  const go = (to) => {
    navigate(to);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <MainContainer hideLogo offPadding>
      <Wrapper>
        <KanjiSide
          as={motion.div}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: [0.22, 0.72, 0.18, 1] }}
        >
          迷
        </KanjiSide>

        <Tape
          as={motion.div}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 0.72, 0.18, 1] }}
        >
          <TapeTrack>
            <span>{TAPE_ITEMS.repeat(4)}</span>
            <span>{TAPE_ITEMS.repeat(4)}</span>
          </TapeTrack>
        </Tape>

        <Content>
          <Code
            data-text="404"
            as={motion.div}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 0.72, 0.18, 1] }}
          >
            404
          </Code>

          <Heading
            as={motion.h1}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.2,
              duration: 0.7,
              ease: [0.22, 0.72, 0.18, 1],
            }}
          >
            Zalutao si<i>.</i>
          </Heading>

          <Text
            as={motion.p}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.35,
              duration: 0.7,
              ease: [0.22, 0.72, 0.18, 1],
            }}
          >
            Stranica koju tražiš ne postoji ili je premeštena.
          </Text>

          <Ctas
            as={motion.div}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.5,
              duration: 0.7,
              ease: [0.22, 0.72, 0.18, 1],
            }}
          >
            <BtnRed onClick={() => go("/")}>
              Nazad na početnu <i>→</i>
            </BtnRed>
            <BtnGhost onClick={() => go("/prodavnica")}>
              Idi u prodavnicu
            </BtnGhost>
          </Ctas>
        </Content>
      </Wrapper>
    </MainContainer>
  );
};

export default NotFoundPage;

const glitchA = keyframes`
  0%, 88%, 100% { clip-path: inset(0 0 100% 0); transform: translate(0, 0); }
  89% { clip-path: inset(12% 0 58% 0); transform: translate(-0.5rem, 0.2rem); }
  91% { clip-path: inset(62% 0 8% 0); transform: translate(0.4rem, -0.2rem); }
  93% { clip-path: inset(30% 0 45% 0); transform: translate(-0.3rem, 0); }
  95% { clip-path: inset(0 0 100% 0); transform: translate(0, 0); }
`;

const glitchB = keyframes`
  0%, 88%, 100% { clip-path: inset(100% 0 0 0); transform: translate(0, 0); }
  90% { clip-path: inset(55% 0 20% 0); transform: translate(0.5rem, -0.2rem); }
  92% { clip-path: inset(8% 0 70% 0); transform: translate(-0.4rem, 0.2rem); }
  94% { clip-path: inset(40% 0 35% 0); transform: translate(0.3rem, 0); }
  96% { clip-path: inset(100% 0 0 0); transform: translate(0, 0); }
`;

const tapeScroll = keyframes`
  to { transform: translateX(-50%); }
`;

const Wrapper = styled.div`
  min-height: 100svh;
  width: 100%;
  padding: 6rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text, #f4f2ee);
  position: relative;
  overflow: hidden;
  background: radial-gradient(
    ellipse 70% 55% at 50% 30%,
    #15151a 0%,
    var(--bg0, #0a0a0c) 65%
  );
`;

const KanjiSide = styled.div`
  position: absolute;
  right: clamp(-4rem, 3vw, 9rem);
  top: 50%;
  transform: translateY(-50%);
  font-family: var(--font-kanji);
  font-size: clamp(24rem, 44vh, 44rem);
  line-height: 0.9;
  writing-mode: vertical-rl;
  color: rgba(254, 95, 85, 0.06);
  -webkit-text-stroke: 2px rgba(254, 95, 85, 0.18);
  pointer-events: none;
  user-select: none;
`;

const Tape = styled.div`
  position: absolute;
  top: 50%;
  left: -10%;
  width: 120%;
  transform: rotate(-4deg);
  background: var(--red, #fe5f55);
  padding: 1.1rem 0;
  overflow: hidden;
  z-index: 0;
  box-shadow: 0 1.6rem 4.4rem -1.6rem rgba(0, 0, 0, 0.8);
`;

const TapeTrack = styled.div`
  display: flex;
  width: max-content;
  animation: ${tapeScroll} 28s linear infinite;

  span {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    font-size: 1.4rem;
    color: var(--bg0, #0a0a0c);
    white-space: nowrap;
    padding-right: 1rem;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Code = styled.div`
  position: relative;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  font-size: clamp(14rem, 26vw, 26rem);
  line-height: 0.9;
  letter-spacing: 0.04em;
  color: var(--text, #f4f2ee);
  text-shadow: 0 1rem 5rem rgba(0, 0, 0, 0.7);
  user-select: none;

  &::before,
  &::after {
    content: attr(data-text);
    position: absolute;
    inset: 0;
    line-height: 0.9;
  }

  &::before {
    color: var(--red, #fe5f55);
    animation: ${glitchA} 3.2s infinite;
  }

  &::after {
    color: rgba(244, 242, 238, 0.75);
    animation: ${glitchB} 3.2s infinite;
  }
`;

const Heading = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(2.6rem, 4.4vw, 4rem);
  line-height: 1;
  letter-spacing: 0.03em;
  margin: 2.6rem 0 1.4rem;

  i {
    font-style: normal;
    color: var(--red, #fe5f55);
  }
`;

const Text = styled.p`
  font-size: 1.5rem;
  font-family: "Poppins-Regular";
  color: var(--muted, #8e8c94);
  line-height: 1.8;
  margin-bottom: 3.2rem;
`;

const Ctas = styled.div`
  display: flex;
  gap: 1.4rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const BtnRed = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.3rem;
  padding: 1.5rem 3rem;
  border-radius: 0.4rem;
  border: none;
  cursor: pointer;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  transition: all 0.3s var(--ease, ease);

  i {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: var(--red-deep, #e04a40);

    i {
      transform: translateX(0.4rem);
    }
  }
`;

const BtnGhost = styled(BtnRed)`
  background: rgba(10, 10, 12, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: var(--text, #f4f2ee);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));

  &:hover {
    background: rgba(10, 10, 12, 0.5);
    border-color: var(--red, #fe5f55);
    color: var(--red, #fe5f55);
  }
`;
